import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Save } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button, Modal } from '../../ui';
import {
  QuotaEnforcementMode,
  type ClientQuota,
  type UpsertClientQuotaInput,
} from '../../gql/types';
import { TextField } from '../shared/FormField';
import { useQuery } from '../../gql/hooks';
import {
  QUOTA_SCOPE_OPTIONS_QUERY,
  type QuotaScopeOptionsResult,
} from '../../gql/operations';

// Create/edit form for a client MONTHLY quota (2026-08-09). One quota per client: optional
// tokens/month + requests/month caps, plus the enforcement mode. WARN (default) lets requests
// continue past the cap and raises a dashboard alert; BLOCK rejects with 429 until the next
// month. Blank caps mean unbounded on that dimension.

export interface ClientQuotaFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing quota to edit, or null to create. */
  quota: ClientQuota | null;
  /** Client ids that already have a monthly quota (hidden from the create picker). */
  takenClientIds: readonly string[];
  loading?: boolean | undefined;
  onSubmit: (input: UpsertClientQuotaInput) => void;
}

/** Parse an optional positive integer field. Blank → null (clears the cap). */
function parseCap(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return Math.trunc(value);
}

function capToString(value: number | null | undefined): string {
  return value == null ? '' : String(value);
}

const MODE_OPTIONS = [
  {
    value: QuotaEnforcementMode.Warn,
    title: 'Warn',
    detail: 'Requests continue past the cap. The dashboard raises an alert.',
  },
  {
    value: QuotaEnforcementMode.Block,
    title: 'Block',
    detail: 'Requests are rejected (429) until the next month once the budget is spent.',
  },
] as const;

export function ClientQuotaFormModal({
  isOpen,
  onClose,
  quota,
  takenClientIds,
  loading = false,
  onSubmit,
}: ClientQuotaFormModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isEdit = quota !== null;

  const [clientId, setClientId] = useState('');
  const [monthlyTokenCap, setMonthlyTokenCap] = useState('');
  const [monthlyRequestCap, setMonthlyRequestCap] = useState('');
  const [mode, setMode] = useState<QuotaEnforcementMode>(QuotaEnforcementMode.Warn);

  const clientOptionsQuery = useQuery<QuotaScopeOptionsResult>(QUOTA_SCOPE_OPTIONS_QUERY);

  useEffect(() => {
    if (!isOpen) return;
    setClientId(quota?.clientId ?? '');
    setMonthlyTokenCap(capToString(quota?.monthlyTokenCap));
    setMonthlyRequestCap(capToString(quota?.monthlyRequestCap));
    setMode(quota?.enforcementMode ?? QuotaEnforcementMode.Warn);
  }, [isOpen, quota]);

  // One monthly quota per client: clients that already have one are excluded from the
  // create picker (editing keeps the current client visible + locked).
  const clientOptions = useMemo(() => {
    const clients = clientOptionsQuery.data?.clients ?? [];
    return clients
      .filter((c) => c.id === quota?.clientId || !takenClientIds.includes(c.id))
      .map((c) => ({ value: c.id, label: `${c.name}  ·  ${c.id}` }));
  }, [clientOptionsQuery.data, quota, takenClientIds]);

  const valid = clientId.trim().length > 0;

  const handleSubmit = () => {
    if (!valid) return;
    const input: UpsertClientQuotaInput = {
      clientId: clientId.trim(),
      monthlyTokenCap: parseCap(monthlyTokenCap),
      monthlyRequestCap: parseCap(monthlyRequestCap),
      enforcementMode: mode,
    };
    if (isEdit && quota) input.id = quota.id;
    onSubmit(input);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit monthly quota' : 'New monthly quota'}
      subtitle="A per-client monthly budget for tokens and requests. Leave a cap blank to leave it unbounded."
      icon={<CalendarClock className="h-7 w-7" aria-hidden="true" />}
      footer={
        <>
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!valid}
            onClick={handleSubmit}
            leftIcon={<Save className="h-4 w-4" aria-hidden="true" />}
          >
            Save monthly quota
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="space-y-1.5">
          <span
            className={`block text-xs font-medium ${
              isDark ? 'text-white/60' : 'text-slate-600'
            }`}
          >
            Client <span aria-hidden="true">*</span>
          </span>
          <AutocompleteSelect
            label="Client"
            value={clientId}
            options={clientOptions}
            onChange={setClientId}
            disabled={isEdit}
            placeholder={
              clientOptionsQuery.loading
                ? 'Loading…'
                : clientOptions.length === 0
                  ? 'Every client already has a monthly quota'
                  : 'Search by name…'
            }
          />
          <span className={`block text-[11px] ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
            One monthly quota per client. {isEdit ? 'The client cannot be changed here.' : ''}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Tokens per month"
            type="number"
            min="1"
            inputMode="numeric"
            value={monthlyTokenCap}
            onChange={(e) => setMonthlyTokenCap(e.target.value)}
            placeholder="Unbounded"
          />
          <TextField
            label="Requests per month"
            type="number"
            min="1"
            inputMode="numeric"
            value={monthlyRequestCap}
            onChange={(e) => setMonthlyRequestCap(e.target.value)}
            placeholder="Unbounded"
          />
        </div>

        <fieldset className="space-y-1.5">
          <legend
            className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-slate-600'}`}
          >
            When the cap is reached
          </legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {MODE_OPTIONS.map((option) => {
              const selected = mode === option.value;
              return (
                <label
                  key={option.value}
                  className={`cursor-pointer rounded-xl border p-3 transition-all duration-200 ${
                    selected
                      ? isDark
                        ? 'border-indigo-400/60 bg-indigo-500/10'
                        : 'border-indigo-400 bg-indigo-50'
                      : isDark
                        ? 'border-white/[0.06] bg-white/[0.02] hover:border-white/20'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="enforcement-mode"
                    className="sr-only"
                    value={option.value}
                    checked={selected}
                    onChange={() => setMode(option.value)}
                  />
                  <span
                    className={`block text-sm font-semibold ${
                      isDark ? 'text-white/90' : 'text-slate-900'
                    }`}
                  >
                    {option.title}
                    {option.value === QuotaEnforcementMode.Warn && (
                      <span
                        className={`ml-2 text-[10px] font-medium uppercase tracking-widest ${
                          isDark ? 'text-white/40' : 'text-slate-400'
                        }`}
                      >
                        default
                      </span>
                    )}
                  </span>
                  <span
                    className={`mt-1 block text-xs leading-snug ${
                      isDark ? 'text-white/50' : 'text-slate-500'
                    }`}
                  >
                    {option.detail}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      </div>
    </Modal>
  );
}
